import React, { useEffect, useState } from 'react';
import './index.less';

const FAQ_ITEMS = [
  {
    id: 'faq-1',
    question: 'Who can register for the DSers Developer Program?',
    answer:
      'The program is intended for developers representing suppliers, supply platforms, sales agents, or sales channels. Applicants should be able to build and maintain an application, understand API authentication and data flows, and complete the DSers review and launch process.',
  },
  {
    id: 'faq-2',
    question: 'What can a DSers Supplier App do?',
    answer:
      'A Supplier App lets DSers merchants source products from a connected supplier platform and process orders with that supplier. The workflow includes product import, order processing, shipment, and tracking information returned through DSers.',
  },
  {
    id: 'faq-3',
    question: 'What can a DSers Channel App do?',
    answer:
      'A Channel App connects a sales channel to DSers. Merchants can move products toward the channel store, synchronize customer orders for supplier processing, and return tracking information through the connected workflow.',
  },
  {
    id: 'faq-4',
    question: 'What happens after I submit my developer registration?',
    answer:
      'DSers reviews the application. Approval time can vary. Once approved, developers can access the relevant documentation, sandbox environment, and app submission guidance described by the DSers Developer Program.',
  },
  {
    id: 'faq-5',
    question: 'Do regular DSers store owners need a developer account?',
    answer:
      'No. Store owners can connect supported partner applications directly in DSers. Developer registration is for teams building a custom Supplier App or Channel App, not for merchants who only want to use an existing integration.',
  },
];

const scrollToHashTarget = (hash) => {
  const id = (hash || '').replace(/^#/, '');
  if (!id) return;
  const target = document.getElementById(id);
  if (!target) return;

  const header = document.querySelector('.site-header, .header_container');
  const offset = (header?.offsetHeight || 72) + 24;
  const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

  if (target.classList.contains('app-card')) {
    target.classList.add('is-targeted');
    window.setTimeout(() => target.classList.remove('is-targeted'), 1800);
  }
};

export default () => {
  const origin = process.env.REACT_APP_DSERS_USER_CENTER_ORIGIN;
  const registerUrl = `${origin}/accounts/register?from=dev`;
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (window.location.hash) {
      const timer = window.setTimeout(() => scrollToHashTarget(window.location.hash), 50);
      return () => window.clearTimeout(timer);
    }
  }, []);

  const toggleFaq = (id) => {
    setOpenFaq((prev) => (prev === id ? null : id));
  };

  return (
    <div className="home-landing">
      <section className="hero">
        <div className="home-container">
          <div className="hero-copy">
            <h1>Build Dropshipping Apps with the DSers API</h1>
            <p className="lead">
              Connect a supplier platform or sales channel to the product, order, and tracking
              workflows used by DSers merchants. DSers Open API gives developers two integration
              paths—Supplier App and Channel App—so each side of the dropshipping stack can
              exchange the data it owns.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href={registerUrl} target="_blank" rel="noreferrer">
                CREATE DEVELOPER ACCOUNT <span aria-hidden="true">↗</span>
              </a>
              <a className="button button-secondary" href="#app-types">
                COMPARE APP TYPES <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>

          <figure className="hero-art">
            <div className="hero-art-media">
              <img
                src="/home/dsers-api-hero-style-test-v3.png"
                width="2172"
                height="724"
                loading="eager"
                alt="Pixel-art diagram of supplier, DSers API, and sales-channel interfaces exchanging product, order, and tracking data"
              />
              <span className="data-cargo product" aria-hidden="true" />
              <span className="data-cargo order" aria-hidden="true" />
              <span className="data-cargo tracking" aria-hidden="true" />
              <span className="hero-walker" aria-hidden="true">
                <img src="/home/pixel-developer-orange.png" width="1254" height="1254" alt="" />
              </span>
            </div>
          </figure>
        </div>
      </section>

      <section className="query-section" aria-labelledby="query-title">
        <div className="home-container">
          <div className="query-panel">
            <h2 id="query-title">An AliExpress API workflow optimized for dropshipping</h2>
            <p className="body-copy">
              The DSers API is built to connect your app with DSers. Within that connection, DSers
              has deeply optimized the AliExpress-related product, order, fulfillment, and tracking
              workflow for real dropshipping operations—so your team can build on a structured
              process instead of recreating every merchant step from scratch.
            </p>
            <p className="body-copy">
              Use the DSers API to bring a Supplier App or Channel App into that optimized workflow.
              Your integration can focus on the data and experience your platform owns while DSers
              helps move products, orders, fulfillment updates, and tracking information through the
              merchant’s connected stack.
            </p>
            <div className="route-grid">
              <div className="route">
                <span className="route-code">OPTIMIZED_FLOW</span>
                <strong>Built around AliExpress dropshipping operations</strong>
                <p>
                  Work with a flow refined for product sourcing, order processing, fulfillment
                  updates, and shipment tracking.
                </p>
              </div>
              <div className="route">
                <span className="route-code">APP_CONNECTION</span>
                <strong>Connect your app to DSers</strong>
                <p>
                  Build a Supplier App or Channel App that adds your platform’s capabilities to the
                  DSers merchant workflow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="app-types" className="section app-section" aria-labelledby="apps-title">
        <div className="home-container">
          <div className="section-head readable">
            <span className="section-kicker">01 // APP ARCHITECTURE</span>
            <h2 id="apps-title">Connect either side of a dropshipping workflow</h2>
            <p className="body-copy">
              Choose the app type according to the platform you operate and the direction product,
              order, and tracking data must move.
            </p>
          </div>
          <div className="app-grid">
            <article id="supplier-app" className="app-card">
              <span className="app-index">APP_TYPE_01</span>
              <h3>Supplier App API</h3>
              <span className="app-for">FOR SUPPLIERS + SUPPLY PLATFORMS</span>
              <p>
                Build an app that lets DSers merchants source products from your platform and
                process orders with you. Merchants can install the approved app, import products, and
                push them to their stores through DSers. When customers order, the flow returns
                through DSers so the supplier can ship and provide tracking information.
              </p>
              <p className="app-result">
                <b>OUTPUT:</b> Your supply platform becomes available inside a DSers merchant
                workflow.
              </p>
              <div className="app-art">
                <img
                  src="/home/supplier-app-pixel.png"
                  width="1448"
                  height="1086"
                  loading="lazy"
                  alt="Pixel-art supplier workflow with product cartons, an API gateway, an order document, and a tracking parcel"
                />
              </div>
            </article>
            <article id="channel-app" className="app-card">
              <span className="app-index">APP_TYPE_02</span>
              <h3>Channel App API</h3>
              <span className="app-for">FOR SALES AGENTS + SALES CHANNELS</span>
              <p>
                Build an app that adds a product distribution path to DSers. Merchants can import
                products from suppliers and push them to your channel store. DSers synchronizes
                customer orders for supplier processing and returns tracking information to the
                channel side.
              </p>
              <p className="app-result">
                <b>OUTPUT:</b> Your sales channel connects product publishing, orders, and tracking
                with DSers.
              </p>
              <div className="app-art">
                <img
                  src="/home/channel-app-pixel.png"
                  width="1448"
                  height="1086"
                  loading="lazy"
                  alt="Pixel-art sales-channel workflow with storefront products, an API gateway, an order document, and a tracking parcel"
                />
              </div>
            </article>
          </div>
          <aside className="code-rail" aria-label="Conceptual developer workflow pseudocode">
            <span className="code-rail-label">{'// CONCEPTUAL_WORKFLOW — NOT SDK SYNTAX'}</span>
            <div className="code-lines">
              <code>
                <span className="token-keyword">type</span> AppPath ={' '}
                <span className="token-string">&quot;supplier&quot;</span> |{' '}
                <span className="token-string">&quot;channel&quot;</span>;
              </code>
              <code>
                <span className="token-keyword">const</span> flow = [
                <span className="token-string">&quot;products&quot;</span>,{' '}
                <span className="token-string">&quot;orders&quot;</span>,{' '}
                <span className="token-string">&quot;tracking&quot;</span>];
              </code>
              <code>
                <span className="token-keyword">if</span> (review.approved){' '}
                <span className="token-method">launch</span>();
              </code>
            </div>
          </aside>
        </div>
      </section>

      <section id="data-flow" className="section data-section" aria-labelledby="data-title">
        <div className="home-container">
          <div className="section-head readable">
            <span className="section-kicker">02 // DATA FLOW</span>
            <h2 id="data-title">How the DSers API connects dropshipping data</h2>
            <p className="body-copy">
              DSers API requests and responses exchange product and order data between DSers and an
              approved app. The direction changes with the app type.
            </p>
          </div>
          <div className="pipeline" aria-label="Supplier App data flow">
            <span className="pipeline-label">SUPPLIER APP PATH</span>
            <div className="pipeline-track">
              <div className="pipeline-step">
                <small>01 / SOURCE</small>
                <strong>Supplier App</strong>
              </div>
              <div className="pipeline-step">
                <small>02 / IMPORT</small>
                <strong>Products</strong>
              </div>
              <div className="pipeline-step">
                <small>03 / WORKFLOW</small>
                <strong>DSers</strong>
              </div>
              <div className="pipeline-step">
                <small>04 / PURCHASE</small>
                <strong>Orders</strong>
              </div>
              <div className="pipeline-step">
                <small>05 / RETURN</small>
                <strong>Tracking</strong>
              </div>
            </div>
          </div>
          <div className="pipeline" aria-label="Channel App data flow">
            <span className="pipeline-label">CHANNEL APP PATH</span>
            <div className="pipeline-track">
              <div className="pipeline-step">
                <small>01 / SOURCE</small>
                <strong>Supplier</strong>
              </div>
              <div className="pipeline-step">
                <small>02 / WORKFLOW</small>
                <strong>DSers</strong>
              </div>
              <div className="pipeline-step">
                <small>03 / DISTRIBUTE</small>
                <strong>Channel App</strong>
              </div>
              <div className="pipeline-step">
                <small>04 / SYNC</small>
                <strong>Orders</strong>
              </div>
              <div className="pipeline-step">
                <small>05 / RETURN</small>
                <strong>Tracking</strong>
              </div>
            </div>
          </div>
          <p className="pipeline-note">
            The dropshipping API layer connects systems responsible for supply, selling, order
            processing, and shipment updates. It does not force the same integration model onto
            every participant.
          </p>
        </div>
      </section>

      <section id="build" className="section timeline-section" aria-labelledby="build-title">
        <div className="home-container">
          <div className="section-head readable">
            <span className="section-kicker">03 // DEVELOPER LIFECYCLE</span>
            <h2 id="build-title">From developer registration to an installed DSers app</h2>
            <p className="body-copy">
              Registration starts the process. Configuration, development, submission, and DSers
              review come before merchants can install the app.
            </p>
          </div>
          <div className="timeline">
            <article className="timeline-item">
              <span className="timeline-code">01 // REGISTER</span>
              <h3>Create a developer account</h3>
              <p>
                Create or sign in to a DSers Developer account. Submit the required team information
                so DSers can review your eligibility for the app development program.
              </p>
            </article>
            <article className="timeline-item">
              <span className="timeline-code">02 // CONFIGURE</span>
              <h3>Set up the app profile</h3>
              <p>
                Create the app profile in the Developer dashboard. Add the required application
                information, API information, and authorization configuration for your Supplier App
                or Channel App.
              </p>
            </article>
            <article className="timeline-item">
              <span className="timeline-code">03 // BUILD + SUBMIT</span>
              <h3>Develop and send the app for review</h3>
              <p>
                Develop and test the integration, complete the required settings, then submit the app
                to DSers. Review and approval times can vary.
              </p>
            </article>
            <article className="timeline-item">
              <span className="timeline-code">04 // PUBLISH + INSTALL</span>
              <h3>Make the approved app available</h3>
              <p>
                After approval and publication, DSers merchants can install the app. The product,
                order, and tracking workflow then operates according to the selected app type.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="section fit-section" aria-labelledby="fit-title">
        <div className="home-container">
          <div className="section-head readable">
            <span className="section-kicker">04 // PROGRAM FIT</span>
            <h2 id="fit-title">Use the DSers API when your platform owns part of the workflow</h2>
            <p className="body-copy">
              The program is designed for teams that provide supply or sales-channel infrastructure
              and can maintain a production API integration.
            </p>
          </div>
          <div className="signal-list">
            <div className="signal">
              <span className="signal-icon" aria-hidden="true">
                S
              </span>
              <div>
                <strong>You operate a supplier or supply platform</strong>
                <p>
                  Build a Supplier App when DSers merchants need to source your products and send
                  orders back for fulfillment.
                </p>
              </div>
            </div>
            <div className="signal">
              <span className="signal-icon" aria-hidden="true">
                C
              </span>
              <div>
                <strong>You operate a sales agency or sales channel</strong>
                <p>
                  Build a Channel App when merchants need to distribute products and synchronize
                  customer orders through DSers.
                </p>
              </div>
            </div>
            <div className="signal">
              <span className="signal-icon" aria-hidden="true">
                D
              </span>
              <div>
                <strong>Your team can own the integration lifecycle</strong>
                <p>
                  Plan for authentication, data flows, application maintenance, submission, review,
                  and launch—not only an initial API call.
                </p>
              </div>
            </div>
          </div>
          <div className="store-owner-note">
            <strong>STORE OWNER?</strong>
            <p>
              If you only want to connect a supported sales channel, you do not need a developer
              account. Use the integrations inside DSers. Otherwise, request support for a platform
              or use available CSV import and export workflows.
            </p>
          </div>
        </div>
      </section>

      <section className="cta-section" aria-labelledby="cta-title">
        <div className="home-container">
          <div className="cta-shell">
            <p className="eyebrow">READY_STATE // TEAM + APP PATH CONFIRMED</p>
            <h2 id="cta-title">Build your dropshipping integration in the DSers app ecosystem</h2>
            <p className="lead">
              Choose the correct app path, register your developer team, and prepare a Supplier App
              or Channel App for DSers review.
            </p>
            <a className="button button-primary" href={registerUrl} target="_blank" rel="noreferrer">
              CREATE DEVELOPER ACCOUNT <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </section>

      <section id="faq" className="faq-section" aria-labelledby="faq-title">
        <div className="home-container">
          <h2 id="faq-title" className="faq-title">
            FAQ
          </h2>
          <div className="faq-list">
            {FAQ_ITEMS.map((item) => {
              const expanded = openFaq === item.id;
              return (
                <div className="faq-item" key={item.id}>
                  <button
                    className="faq-question"
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={`${item.id}-answer`}
                    id={`${item.id}-question`}
                    onClick={() => toggleFaq(item.id)}
                  >
                    <span>{item.question}</span>
                    <span className="faq-icon" aria-hidden="true">
                      <span className="faq-icon__bar faq-icon__bar--h" />
                      <span className="faq-icon__bar faq-icon__bar--v" />
                    </span>
                  </button>
                  <div
                    className="faq-answer"
                    id={`${item.id}-answer`}
                    role="region"
                    aria-labelledby={`${item.id}-question`}
                    data-open={expanded}
                  >
                    <div>
                      <p>{item.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
